import { TableBody, TableRow } from '@mui/material';
import { useIsMobile } from '@parrot-software/hooks';
import { Checkbox, Table, TableHead, TableHeader } from '@parrot-software/design-system';
import { useTranslation } from 'react-i18next';

import type { EmployeeDeleteTarget, EmployeeListItem } from '@personnel/presentation/types';
import { EmployeesMobileList } from './EmployeesMobileList';
import { EmployeesMobileListSkeleton } from './EmployeesMobileListSkeleton';
import { EmployeesTableRow } from './EmployeesTableRow';
import { EmployeesTableSkeleton } from './EmployeesTableSkeleton';

interface EmployeesTableProps {
  employees: EmployeeListItem[];
  isLoading?: boolean;
  onDeleteEmployee?: (employee?: EmployeeDeleteTarget) => void;
  selectedEmployees: { uuid: string }[];
  onToggleAll: (checked: boolean) => void;
  onToggleOne: (employee: EmployeeListItem, checked: boolean) => void;
}

const styles = {
  emptyState: {
    wrapper: 'flex items-center justify-center py-12',
    content: 'text-center',
    title: 'text-sm text-muted-foreground',
    subtitle: 'text-xs text-muted-foreground mt-1',
  },
  table: 'w-full min-w-[920px] table-fixed',
  header: {
    container: 'bg-muted border-b border-border',
  },
  // Proportional column widths; text columns truncate, numeric/action columns stay right-aligned.
  // Actions is fixed to the size of its icon button so it never steals room from the text columns.
  columns: {
    checkbox: 'w-12 text-left',
    name: 'w-[18%] text-left',
    email: 'w-[24%] text-left',
    role: 'w-[22%] text-left',
    status: 'w-[16%] text-left',
    stores: 'w-[10%] text-right',
    actions: 'w-16 px-2 text-right',
  },
};

/**
 * The users list: a table from `md` up, a stack of cards below it.
 *
 * The two render the same records and share the same selection, so a bulk delete behaves the same
 * either way. Between the two the table scrolls sideways rather than shrinking its columns, because
 * a squeezed email column reads as a different address.
 *
 * @param {EmployeesTableProps} props - Component properties
 * @returns {React.ReactElement} Employees list
 */
export const EmployeesTable = ({
  employees,
  isLoading = false,
  onDeleteEmployee,
  selectedEmployees,
  onToggleAll,
  onToggleOne,
}: EmployeesTableProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const isMobile = useIsMobile();

  const areAllSelected =
    employees.length > 0 && employees.every((e) => selectedEmployees.some((s) => s.uuid === e.uuid));

  if (isLoading) {
    return isMobile ? <EmployeesMobileListSkeleton /> : <EmployeesTableSkeleton />;
  }

  if (employees.length === 0) {
    return (
      <div className={styles.emptyState.wrapper}>
        <div className={styles.emptyState.content}>
          <p className={styles.emptyState.title}>{t('list.emptyState.title')}</p>
          <p className={styles.emptyState.subtitle}>{t('list.emptyState.description')}</p>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <EmployeesMobileList
        areAllSelected={areAllSelected}
        employees={employees}
        onDeleteEmployee={onDeleteEmployee}
        onToggleAll={onToggleAll}
        onToggleOne={onToggleOne}
        selectedEmployees={selectedEmployees}
      />
    );
  }

  return (
    <div className="w-full min-w-0 overflow-x-auto rounded-md border">
      <Table className={styles.table} wrapperClassName="overflow-visible">
        <TableHeader className={styles.header.container}>
          <TableRow>
            <TableHead className={styles.columns.checkbox}>
              <Checkbox checked={areAllSelected} onCheckedChange={onToggleAll} />
            </TableHead>
            <TableHead className={styles.columns.name}>{t('list.table.columns.name')}</TableHead>
            <TableHead className={styles.columns.email}>{t('list.table.columns.email')}</TableHead>
            <TableHead className={styles.columns.role}>{t('list.table.columns.role')}</TableHead>
            <TableHead className={styles.columns.status}>{t('list.table.columns.status')}</TableHead>
            <TableHead className={styles.columns.stores}>{t('list.table.columns.stores')}</TableHead>
            <TableHead className={styles.columns.actions} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {employees.map((employee) => (
            <EmployeesTableRow
              checked={selectedEmployees.some((s) => s.uuid === employee.uuid)}
              employee={employee}
              key={employee.uuid}
              onCheckedChange={onToggleOne}
              onDelete={onDeleteEmployee}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
